/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Card, Grid, LinearProgress } from '@mui/material';
import React from 'react';
import { dateUtils } from '@opengeoweb/shared';
import { createDataset } from './createChartSeriesData';
import { GraphErrors } from './GraphErrors';
import { Graphs } from './Graphs';
import {
  useUserSleeping,
  getGraphHeightInPx,
  createStreamDict,
} from './TimeSeries.utils';
import { TimeSeriesHeader } from './TimeSeriesHeader';
import type { Dataset } from './types';
import {
  CHART_TOP_MARGIN,
  CHART_BOTTOM_MARGIN,
  PLOT_TOP_MARGIN,
  config,
  CHART_LEFT_MARGIN,
  CHART_RIGHT_MARGIN,
} from './utils';
import {
  getBeginTime,
  getEndTime,
  endTime,
  beginTime,
} from '../../utils/defaultTimeRange';
import {
  useGetStreams,
  useGetTimeSeries,
} from '../../utils/spaceweather-api/hooks';

// eslint-disable-next-line @typescript-eslint/naming-convention
const time_start = dateUtils.dateToString(beginTime)!;
// eslint-disable-next-line @typescript-eslint/naming-convention
const time_stop = dateUtils.dateToString(endTime)!;

const timeSeriesDefaultOptions = config
  .map((graphItem) =>
    graphItem.params.map((param) => ({
      ...param,
      time_start,
      time_stop,
    })),
  )
  .flat();
const defaultErrorData = config.map((graphItem) => {
  const parameters = graphItem.params.map((param) => param.parameter);
  return {
    parameters,
    error: '',
  };
});

// component
const TimeSeries: React.FC = () => {
  const [isUserSleeping, resetUserIsSleeping] = useUserSleeping();

  // only refetch if user is sleeping
  const { data: streamsData } = useGetStreams(!isUserSleeping);

  const onUserAction = (): void => {
    resetUserIsSleeping();
  };

  const [graphData, setGraphData] = React.useState(config);
  const [errorData, setErrorData] = React.useState(defaultErrorData);
  const [timeSeriesOptions, setTimeSeriesOptions] = React.useState(
    timeSeriesDefaultOptions,
  );
  const timeSeriesResults = useGetTimeSeries(timeSeriesOptions);

  React.useEffect(() => {
    if (!timeSeriesResults) {
      return;
    }

    setErrorData((prevErrorData) => {
      const newErrorData = prevErrorData.map((item) => {
        const matchingResult = timeSeriesResults.find((_, index) =>
          item.parameters.includes(timeSeriesOptions[index].parameter),
        );

        if (matchingResult?.error) {
          return { ...item, error: matchingResult.error.message };
        }
        return item;
      });
      if (JSON.stringify(newErrorData) === JSON.stringify(prevErrorData)) {
        return prevErrorData;
      }
      return newErrorData;
    });

    setGraphData((prevGraphData) => {
      const newGraphData = prevGraphData.map((item) => {
        const updatedSeries = item.params.reduce<Dataset[]>((acc, params) => {
          const matchingResult = timeSeriesResults.find(
            (_, index) =>
              params.parameter === timeSeriesOptions[index].parameter,
          );
          if (matchingResult?.data) {
            return [...acc, createDataset(matchingResult.data.data)];
          }
          return acc;
        }, []);

        if (
          JSON.stringify(updatedSeries) === JSON.stringify(item.series) ||
          !updatedSeries.length
        ) {
          return item;
        }
        return {
          ...item,
          series: updatedSeries,
        };
      });
      if (JSON.stringify(newGraphData) === JSON.stringify(prevGraphData)) {
        return prevGraphData;
      }
      return newGraphData;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSeriesResults]);

  React.useEffect(() => {
    if (isUserSleeping) {
      // automatically jump to current date when fell asleep
      const newTimeStart = dateUtils.dateToString(getBeginTime())!;
      const newTimeEnd = dateUtils.dateToString(getEndTime())!;

      setTimeSeriesOptions((prevOptions) =>
        prevOptions.map((option) => {
          return { ...option, time_start: newTimeStart, time_stop: newTimeEnd };
        }),
      );
    }
  }, [isUserSleeping]);

  React.useEffect(() => {
    if (streamsData) {
      const loadedStreams = createStreamDict(streamsData);
      // find and update charts with new data
      setTimeSeriesOptions((prevOptions) =>
        prevOptions.flatMap((option) => {
          const newStream = loadedStreams[option.stream!];
          // stream has newer data, update the chart
          return newStream ? [{ ...option, time_stop: newStream.end }] : [];
        }),
      );
    }
  }, [streamsData]);

  const isLoading =
    timeSeriesResults.filter((result) => result.isPending).length > 0;

  const graphHeight = getGraphHeightInPx();
  const chartHeight =
    CHART_TOP_MARGIN +
    (PLOT_TOP_MARGIN + graphHeight) * config.length +
    CHART_BOTTOM_MARGIN;

  return (
    <Grid
      container
      sx={{
        backgroundColor: 'geowebColors.background.surfaceApp',
      }}
    >
      <Grid size={12}>
        <TimeSeriesHeader />
      </Grid>
      <Grid
        size={12}
        sx={{ paddingTop: '0px!important', paddingBottom: '4px' }}
      >
        {isLoading ? (
          <LinearProgress
            color="secondary"
            data-testid="timeline-loadingbar"
            sx={{
              marginLeft: `${CHART_LEFT_MARGIN}px`,
              marginRight: `${CHART_RIGHT_MARGIN}px`,
            }}
          />
        ) : (
          <Box sx={{ height: 4 }} />
        )}
      </Grid>
      <Grid size={12} sx={{ height: chartHeight }}>
        <Card
          sx={{
            height: chartHeight,
            border: 'solid 1px',
            borderColor: 'geowebColors.cards.cardContainerBorder',
            boxShadow: 'none',
          }}
        >
          <Graphs
            beginTime={beginTime}
            chartHeight={chartHeight}
            data={graphData}
            endTime={endTime}
            graphHeight={graphHeight}
            onUserAction={onUserAction}
          />
          <GraphErrors
            chartHeight={chartHeight}
            data={errorData}
            graphHeight={graphHeight}
          />
        </Card>
      </Grid>
    </Grid>
  );
};

export default TimeSeries;
